import { IOptimaTreeNode } from '@optima-stride-ai/core';
import { Explorer, IExplorerAttributes, IExplorerNavAttributes } from '@optima-stride-ai/optima-components';

type ExplorerNav = NonNullable<Explorer['explorerNav']>;

// Last item whose path was auto-expanded, per nav instance, so later refreshes keep a user's manual collapse.
const lastRevealedItemByNav = new WeakMap<ExplorerNav, string>();

/**
 * Drop-in replacement for `explorer.updateNavData()`. The rebuild creates new tree nodes and
 * p-tree matches selection by node reference, so the highlighted item is re-bound afterwards.
 */
export function refreshExplorerNav(explorer: Explorer | undefined): void {
  explorer?.updateNavData();
  revealSelectedNavNode(explorer);
}

/** Highlights the nav's current item and, the first time it is selected, expands its path. */
export function revealSelectedNavNode(explorer: Explorer | undefined): void {
  const nav = explorer?.explorerNav;
  const selectedId = nav?.explorerNavAttributes.currentSelectedItem?.toString();
  if (!nav || !selectedId) {
    return;
  }
  const path = findNodePath(nav.hierarchyNodes, selectedId);
  if (!path) {
    return;
  }
  if (lastRevealedItemByNav.get(nav) !== selectedId) {
    path.forEach(node => node.expanded = true);
    lastRevealedItemByNav.set(nav, selectedId);
  }
  nav.selectedNode = path[path.length - 1];
}

/**
 * Replaces `dataRefreshedToken = new Date().getTime().toString()`. Also hands the nav a new attributes
 * reference so the library re-binds its highlight after the token-driven rebuild (Hayagriva pattern).
 */
export function refreshExplorer(host: {
  dataRefreshedToken: string;
  explorerAttributes: IExplorerAttributes;
  explorerNavAttributes: IExplorerNavAttributes;
}): void {
  // The token must always change: the library only picks up the new nav reference on a token change.
  const token = Date.now().toString();
  host.dataRefreshedToken = token === host.dataRefreshedToken ? `${token}-1` : token;
  host.explorerNavAttributes = { ...host.explorerNavAttributes };
  host.explorerAttributes.explorerNavAttributes = host.explorerNavAttributes;
}

function findNodePath(nodes: IOptimaTreeNode[], id: string): IOptimaTreeNode[] | null {
  for (const node of nodes) {
    if (node.data?.id?.toString() === id) {
      return [node];
    }
    const childPath = node.children?.length ? findNodePath(node.children, id) : null;
    if (childPath) {
      return [node, ...childPath];
    }
  }
  return null;
}
